import { useEffect, useMemo, useState } from 'react'
import { Loader2, Lock } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  fetchEmailTemplate,
  updateApplicationStatus,
  sendDeniedApplicationResendEmail,
  type EmailTemplateResponse,
} from '../../services/applicationService'
import { showSuccess, showError } from '@/lib/utils/notifications'
import { trackApplicationEvent } from '@/lib/utils/analytics'
import modalStyles from '../../styles/modals.module.css'
import type { DeniedStatusModalProps, ReasonType, DeniedTemplateGroup } from '../../types/modals'





/** API may return keyed objects (PHP-style maps) instead of arrays — match Angular `keyvalue` usage. */
function asArray<T>(v: unknown): T[] {
  if (v == null) return []
  if (Array.isArray(v)) return v as T[]
  if (typeof v === 'object') return Object.values(v as Record<string, T>)
  return []
}

function extractCheckboxDataRoot(template: Record<string, unknown>): unknown {
  const data = template.data as Record<string, unknown> | undefined
  return (
    template.checkbox_data ??
    data?.checkbox_data ??
    (data?.data as Record<string, unknown> | undefined)?.checkbox_data
  )
}

function normalizeDeniedGroups(template: EmailTemplateResponse): DeniedTemplateGroup[] {
  const t = template as Record<string, unknown>
  const rawTop = extractCheckboxDataRoot(t)
  let raw: Record<string, unknown>[] = []
  if (Array.isArray(rawTop)) {
    raw = rawTop as Record<string, unknown>[]
  } else if (rawTop && typeof rawTop === 'object') {
    const vals = Object.values(rawTop as Record<string, unknown>)
    const first = vals[0] as Record<string, unknown> | undefined
    const childCount =
      first != null && typeof first === 'object'
        ? asArray<unknown>(first.child).length + asArray<unknown>(first.children).length
        : 0
    if (vals.length > 0 && childCount > 0) {
      raw = vals as Record<string, unknown>[]
    }
  }
  return raw
    .map((g, i: number) => {
      const childrenRaw = asArray<Record<string, unknown>>(g.child ?? g.children)
      return {
        key: String(g.key ?? g.name ?? `group_${i}`),
        label: (g.label ?? '') as string,
        children: childrenRaw.map((c, j: number) => ({
          key: String(c.key ?? c.name ?? `${g.key ?? 'group'}_${j}`),
          label: (c.label ?? '') as string,
          description: c.description as string | undefined,
          hasInput: !!c.hasInput,
          isLock: Number(c.isLock) === 1 || c.isLock === true || String(c.isLock) === '1' ? 1 : 0,
        })),
      }
    })
    .filter((g) => g.children.length > 0)
}

function rowKey(groupKey: string, childKey: string) {
  return `${groupKey}.${childKey}`
}

/** Denied flow — Angular SetApplicantStatusComponent DENIED + updateStatusDenied payload. */
export function DeniedStatusModal({
  applicantId,
  applicationAllData: _applicationAllData,
  applicationData,
  onClose,
  onSuccess,
  purpose = 'deny_applicant',
}: DeniedStatusModalProps) {
  const tid = 'application-modal-denied-status';

  const [groups, setGroups] = useState<DeniedTemplateGroup[]>([])
  const [loadingTemplate, setLoadingTemplate] = useState(true)
  const [templateError, setTemplateError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [inputs, setInputs] = useState<Record<string, string>>({})
  const [notes, setNotes] = useState('')
  const [reasonType, setReasonType] = useState<ReasonType>('without_reason')
  const [loading, setLoading] = useState(false)

  const isResendEmail = purpose === 'resend_denied_email'
  const modalTitle = isResendEmail ? 'Denied Application Email' : 'Denied Application'
  const submitButtonLabel = isResendEmail ? 'Send Denied Email' : 'Denied'
  const successMessage = isResendEmail ? 'Email Send Successfully!' : 'Status Changed Successfully!'
  const submitErrorMessage = isResendEmail ? 'Failed to send denial email' : 'Failed to deny application'

  useEffect(() => {
    let cancelled = false
    setLoadingTemplate(true)
    setTemplateError(null)
    fetchEmailTemplate('DENIED_APPLICATION')
      .then((tpl) => {
        if (cancelled) return
        setGroups(normalizeDeniedGroups(tpl))
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = (err as { message?: unknown })?.message
          setTemplateError(typeof msg === 'string' && msg ? msg : 'Failed to load denial template.')
          setGroups([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  /** Angular `setCheckBoxContent` prefill from `applicationData.denied_checkbox_data`. */
  const deniedSaved = applicationData?.denied_checkbox_data
  useEffect(() => {
    if (!groups.length || !deniedSaved || typeof deniedSaved !== 'object') return
    const next: Record<string, boolean> = {}
    for (const g of groups) {
      const saved = deniedSaved[g.key] as { child?: unknown } | undefined
      const savedChildren = asArray<{ key?: string }>(saved?.child)
      if (!savedChildren.length) continue
      for (const child of g.children) {
        const hit = savedChildren.some((s) => s && String(s.key) === String(child.key))
        if (hit) next[rowKey(g.key, child.key)] = true
      }
    }
    setSelected((prev) => ({ ...prev, ...next }))
  }, [groups, deniedSaved])

  const toggleChild = (groupKey: string, childKey: string) => {
    const k = rowKey(groupKey, childKey)
    setSelected((prev) => ({ ...prev, [k]: !prev[k] }))
  }

  const setChildInput = (groupKey: string, childKey: string, value: string) => {
    const k = rowKey(groupKey, childKey)
    setInputs((prev) => ({ ...prev, [k]: value }))
  }

  const checkboxPayload = useMemo(() => {
    const checkElements: Record<string, { key?: string; label?: string; child: Array<Record<string, unknown>> }> = {}
    for (const g of groups) {
      for (const c of g.children) {
        if (!selected[rowKey(g.key, c.key)]) continue
        if (!checkElements[g.key]) {
          checkElements[g.key] = { key: g.key, label: g.label, child: [] }
        }
        const data: Record<string, unknown> = {
          key: c.key,
          label: c.label,
          description: c.description ?? '',
          isLock: c.isLock ?? 0,
        }
        const iv = inputs[rowKey(g.key, c.key)]
        if (c.hasInput && iv?.trim()) {
          data.inputvalue = iv.trim()
        }
        checkElements[g.key].child.push(data)
      }
    }
    return checkElements
  }, [groups, selected, inputs])

  const handleSubmit = async () => {
    setLoading(true)
    try {
      if (isResendEmail) {
        await sendDeniedApplicationResendEmail(applicantId, {
          application_status: 'DENIED_APPLICATION',
          send_cancelled_mail: '',
          reason_type: reasonType,
          notes,
          checkbox_data: checkboxPayload,
        })
      } else {
        await updateApplicationStatus(applicantId, {
          applicant_status: 'DENIED',
          send_cancelled_mail: '',
          reason_type: reasonType,
          notes,
          checkbox_data: checkboxPayload,
        })
        trackApplicationEvent('application_status_updated', 'application_review', {
          application_status: 'denied',
        })
      }
      showSuccess(successMessage)
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message
      showError(typeof msg === 'string' && msg ? msg : submitErrorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={true}
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
      title={modalTitle}
      onClose={onClose}
      maxWidth="max-w-xl"
      headerVariant="centeredTitle"
      bodyScrollOnly
      footer={
        <div className={modalStyles.modalFooterActionGroup}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || loadingTemplate}
            className={modalStyles.modalActionPrimary}
            data-testid={`${tid}-submit-button`}
          >
            {loading ? 'Processing...' : submitButtonLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={modalStyles.modalActionSecondary}
            data-testid={`${tid}-cancel-button`}
          >
            Cancel
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-8 text-sm text-slate-800" data-testid={`${tid}-body`}>
        {loadingTemplate && (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-[#1597ff]" />
            <span>Loading denial options…</span>
          </div>
        )}

        {templateError && !loadingTemplate && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 text-sm">
            {templateError} You can still submit with notes and email options below; checkbox reasons
            will be empty.
          </div>
        )}

        {!loadingTemplate && groups.length > 0 && (
          <div className="flex flex-col gap-8">
            {groups.map((g) => (
              <div key={g.key} className="flex flex-col gap-4">
                <div
                  className="text-sm font-semibold text-slate-700 [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: g.label }}
                />
                <div className="flex flex-col gap-4 border-l-2 border-slate-200 pl-3">
                  {g.children.map((c) => {
                    const rk = rowKey(g.key, c.key)
                    const checked = !!selected[rk]
                    return (
                      <div key={c.key} className="space-y-1" data-testid={`${tid}-group-${g.key}-child-${c.key}`}>
                        <label className="flex cursor-pointer items-start gap-2 text-slate-800" data-testid={`${tid}-group-${g.key}-child-${c.key}-label`}>
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300"
                            checked={checked}
                            onChange={() => toggleChild(g.key, c.key)}
                            data-testid={`${tid}-group-${g.key}-child-${c.key}-checkbox`}
                          />
                          <span className="min-w-0 flex-1 [&_p]:m-0">
                            <span className="inline-flex flex-wrap items-start gap-1.5">
                              <span
                                className="[&_a]:text-[#1597ff] [&_p]:m-0"
                                dangerouslySetInnerHTML={{ __html: c.label }}
                              />
                              {c.isLock === 1 && (
                                <Lock
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500"
                                  aria-label="Locked reason"
                                />
                              )}
                            </span>
                          </span>
                        </label>
                        {c.description && String(c.description).trim().length > 0 && (
                          <div
                            className="ml-6 text-xs text-slate-600 [&_p]:m-0 [&_a]:text-[#1597ff]"
                            dangerouslySetInnerHTML={{ __html: c.description }}
                          />
                        )}
                        {c.hasInput && checked && (
                          <div className="ml-6 mt-1 max-w-xs space-y-1">
                            <label className="block text-xs font-medium text-slate-600" data-testid={`${tid}-group-${g.key}-child-${c.key}-number-label`}>
                              Number* (For example: 1,2)
                            </label>
                            <input
                              type="text"
                              className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                              value={inputs[rk] ?? ''}
                              onChange={(e) => setChildInput(g.key, c.key, e.target.value)}
                              data-testid={`${tid}-group-${g.key}-child-${c.key}-number-input`}
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-[#64748b]" htmlFor="denied-notes" data-testid={`${tid}-notes-label`}>
            Other/Additional info
          </label>
          <textarea
            id="denied-notes"
            rows={4}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-[#1597ff] focus:outline-none focus:ring-1 focus:ring-[#1597ff]"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid={`${tid}-notes-textarea`}
          />
        </div>

        <fieldset className="m-0 border-0 p-0">
          <legend className="sr-only">Denial email options</legend>
          <div className="grid w-full grid-cols-3 gap-x-2 sm:gap-x-4">
            <label className="flex cursor-pointer items-start gap-2 text-left text-[11px] leading-snug text-slate-800 sm:text-xs md:text-sm" data-testid={`${tid}-email-with-reason-label`}>
              <input
                type="radio"
                name="denied_reason_type"
                value="with_reason"
                checked={reasonType === 'with_reason'}
                onChange={() => setReasonType('with_reason')}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#1597ff] sm:h-4 sm:w-4"
                data-testid={`${tid}-email-with-reason-radio`}
              />
              <span className="min-w-0 break-words">Send With Reason Email</span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-left text-[11px] leading-snug text-slate-800 sm:text-xs md:text-sm" data-testid={`${tid}-email-without-reason-label`}>
              <input
                type="radio"
                name="denied_reason_type"
                value="without_reason"
                checked={reasonType === 'without_reason'}
                onChange={() => setReasonType('without_reason')}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#1597ff] sm:h-4 sm:w-4"
                data-testid={`${tid}-email-without-reason-radio`}
              />
              <span className="min-w-0 break-words">Send Without Reason Email</span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-left text-[11px] leading-snug text-slate-800 sm:text-xs md:text-sm" data-testid={`${tid}-email-no-send-label`}>
              <input
                type="radio"
                name="denied_reason_type"
                value="no_reason"
                checked={reasonType === 'no_reason'}
                onChange={() => setReasonType('no_reason')}
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#1597ff] sm:h-4 sm:w-4"
                data-testid={`${tid}-email-no-send-radio`}
              />
              <span className="min-w-0 break-words">Don&apos;t send email</span>
            </label>
          </div>
        </fieldset>
      </div>
    </OnScreenModal>
  )
}
